import { useQuery } from '@tanstack/react-query'
import { api, type CommonCode, type Limits, type Region } from './api'

/**
 * 코드 라벨을 화면에 하드코딩하지 않는다.
 * `resource`는 0821 최최종 입력카드가 근거지만 「기타」 한 자리는 이티랩 확인 대기이고,
 * `service_area`는 C-3 확인 대기다(입력항목 정의표 §5). 테이블에서 읽으면 재배포 없이 반영된다.
 * 폐기된 `help_type`은 `is_active=false`라 이 훅의 기본 조회에 아예 안 나온다(022).
 */

export function useCommonCodes(group?: string) {
  return useQuery({
    queryKey: ['common-codes', group ?? 'all'],
    queryFn: () => api.commonCodes(group),
    staleTime: 10 * 60 * 1000,
    select: (data) => data.items,
  })
}

/**
 * 대상은 한 축이 아니라 **두 축**이다 — 생애주기와 상황·특성(설계서 D-01·C-02).
 * 입력 화면은 두 축을 나눠 묻지만, 저장은 `target_codes` 한 배열이다
 * (배점이 대상을 한 항목으로 채점하므로 축은 입력 UX의 구분일 뿐이다).
 */
export const TARGET_LIFECYCLE = 'target_lifecycle'
export const TARGET_SITUATION = 'target_situation'

/**
 * 두 축을 한 목록으로 합친다 — 라벨 표시·필터처럼 축 구분이 필요 없는 곳에서 쓴다.
 * 축별 쿼리를 그대로 재사용하므로 입력 화면과 캐시를 공유한다.
 */
export function useTargetCodes(): { data: CommonCode[] | undefined; isPending: boolean } {
  const lifecycle = useCommonCodes(TARGET_LIFECYCLE)
  const situation = useCommonCodes(TARGET_SITUATION)
  return {
    data:
      lifecycle.data && situation.data ? [...lifecycle.data, ...situation.data] : undefined,
    isPending: lifecycle.isPending || situation.isPending,
  }
}

/**
 * 갯수 상한(030) — 관리자 설정에서 온다. 코드값 라벨과 같은 이유로 서버에서 읽는다.
 *
 * **폴백을 두는 이유**: 첫 렌더는 응답 전이고, 조회가 실패할 수도 있다. 그때 상한을 0으로
 * 보면 아무것도 못 고르는 화면이 되므로 알려진 초기값으로 그린다(api `LIMIT_FALLBACK`과
 * 같은 값 — 여기가 정본이 아니다. 운영값을 바꾸는 곳은 관리자 화면이다).
 * 서버는 요청마다 자기 설정으로 다시 검사하므로, 화면이 잠깐 옛 상한을 그려도 데이터는 안 샌다.
 */
export const LIMITS_FALLBACK: Limits = {
  categoryMax: 2,
  resourceSuggestMax: 3,
  solutionCardPerOrg: 3,
}

export function useLimits(): Limits {
  const q = useQuery({
    queryKey: ['settings-limits'],
    queryFn: () => api.limits(),
    staleTime: 10 * 60 * 1000,
    select: (data) => data.limits,
  })
  return q.data ?? LIMITS_FALLBACK
}

export function useRegions() {
  return useQuery({
    queryKey: ['regions'],
    queryFn: () => api.regions(),
    staleTime: 10 * 60 * 1000,
    select: (data) => data.items,
  })
}

/**
 * 읍면동(`emd`)만 — 시군구 레코드는 선택지에서 뺀다.
 *
 * 레벨 값은 014에서 `dong`/`city` → `emd`/`sigungu`로 바뀌었다. 전국엔 읍·면이 있어
 * `dong`은 곧 거짓이 되는 이름이었다.
 */
export function useDongs() {
  const q = useRegions()
  return { ...q, data: q.data?.filter((r) => r.level === 'emd') }
}

export type CodeLabeler = (code: string) => string

/**
 * 라벨을 못 찾으면 코드값을 그대로 보여준다.
 * 빈 문자열로 감추면 "정보 없음"과 구분되지 않는다 — 시드 누락을 화면에서 바로 알아채야 한다.
 */
export function makeLabeler(codes: CommonCode[] | undefined): CodeLabeler {
  const map = new Map((codes ?? []).map((c) => [c.code, c.label]))
  return (code) => map.get(code) ?? code
}

export function makeRegionLabeler(regions: Region[] | undefined): CodeLabeler {
  const map = new Map((regions ?? []).map((r) => [r.code, r.name]))
  return (code) => map.get(code) ?? code
}

export const URGENCY_LABEL: Record<string, string> = {
  high: '시급',
  mid: '보통',
  low: '여유',
}

export const CERT_LABEL: Record<string, string> = {
  cert_certified: '인증',
  cert_preliminary: '예비',
}
